import { createSlice } from "@reduxjs/toolkit";
import storage from "@/services/storage";
import config from "@/config";
import ApiServices from "@/api/apexchat.api.services";
import {
  LOGIN,
  RESETPASSWORD,
  CHANGEPASSWORD,
  JWT_VERIFY,
  HostAPI,
  ExchangeRefereshToken,
} from "@/api/endpoints";
import {
  clearAuthCookies,
  getCookie,
  removeKeysFromLS,
 setCookie } from "@/services/generic.methods";


/**
 * @name @createSlice
 * @description create redux store slice for auth
 **/

const removeChatbot = () => {
  var chatbot = document.getElementById("apexchat_prechat_invitation_wrapper");
  if (chatbot) chatbot.style.display = "none";
};

const clearStorage = () => {
  let keysArray = ["Profile", "dashboard_order"];
  delete ApiServices.instance.defaults.headers.common["Authorization"];
  removeKeysFromLS(keysArray);
  clearAuthCookies();
};

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    profile: null,
    company: 2,
    userCompaniesCount: 1,
    isAuthenticated: !!getCookie("CRM-Auth"),
    dynamicMeta: null,
    sessionExpired: false,
  },

  reducers: {
    /**
     * @name @login
     * @description login reducer to call login async API with axios instance
     * @requires ApiServices.get method, state and API payload
     **/
    login: (state, action) => {
      let user = action.payload;
      state.isAuthenticated = !!user?.AccessToken;
      state.profile = user;
      setCookie("CRM-Auth", user?.AccessToken, config.authCookieTimeoutInDays);
      setCookie("companyKey", user?.CompanyKey, config.authCookieTimeoutInDays);
      setCookie("companyId", user?.CompanyID, config.authCookieTimeoutInDays);
      setCookie(
        "companyName",
        user?.CompanyName,
        config.authCookieTimeoutInDays
      );
      storage.setItem("Profile", user);
    },
    logout: (state, action) => {
      clearStorage();
      state.isAuthenticated = false;
      state.profile = null;
      state.dropdownOptions = null;
    },
    setProfileToState: (state) => {
      let profile = storage.getItem("Profile");
      if (profile) state.profile = JSON.parse(profile);
    },
    setSessionExpire: (state, action) => {
      state.sessionExpired = action.payload;
    },
    setUpdatedProfileToState: (state, action) => {
      let profile = JSON.parse(storage.getItem("Profile"));
      profile = { ...profile, ...(action.payload || {}) };
      if (profile) state.profile = profile;
      storage.setItem("Profile", profile);
    },
    setLoggedInCompanyToState: (state, action) => {
      state.company = action.payload;
    },
    setDynamicMetaToState: (state, action) => {
      state.dynamicMeta = action.payload;
    },
    setUserCompaniesCountToState: (state, action) => {
      state.userCompaniesCount = action.payload;
    },
  },
});

export const {
  login,
  logout,
  setSessionExpire,
  setProfileToState,
  setDynamicMetaToState,
  setLoggedInCompanyToState,
  setUpdatedProfileToState,
  setUserCompaniesCountToState,
} = authSlice.actions;

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const loginAsync = (payload) => async (dispatch) => {
  delete payload.rememberMe;
  return ApiServices.get(LOGIN, payload)
    .then(async ({ data }) => {
      if (data?.success && data?.data?.JWT) {
        await dispatch(login(data.data));
        return Promise.resolve("resolved");
      }
      return Promise.reject("error");
    })
    .catch((error) => {
      let err = error?.response?.data?.error;
      return Promise.reject(err);
    });
};

export const forgotPasswordAsync = (payload) => async (dispatch) => {
  let resetPwEndPoint = RESETPASSWORD(payload);
  return ApiServices.put(resetPwEndPoint, payload)
    .then(async ({ data }) => {
      if (data.success) {
        return Promise.resolve(data.data);
      }
      return Promise.reject("rejected");
    })
    .catch((error) => {
      return Promise.reject("rejected");
    });
};

/**
 * These function allows to perform async logic for changepassword
 * It gets username from profile in storage
 * Assigning require password rest false after success
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const changePasswordAsync = (payload) => async (dispatch) => {
  let profile = storage.getItem("Profile");
  profile = profile && JSON.parse(profile);
  // setting some prerequisite for the API endpoint
  // {
  //   "userName": "string",
  //   "currentPassword": "string",
  //   "newPassword": "string",
  //   "requirePasswordReset": boolean
  // }
  payload.currentPassword = payload.oldPassword;
  payload.userName = profile.Username;
  payload.requirePasswordReset = false;
  let changePwEndPoint = CHANGEPASSWORD(payload);
  return ApiServices.put(changePwEndPoint, payload)
    .then(async ({ data }) => {
      if (data.success) {
        profile.RequirePasswordReset = false;
        storage.setItem("Profile", profile);
        return Promise.resolve(data.data);
      }
      return Promise.reject("rejected");
    })
    .catch((error) => {
      return Promise.reject("rejected");
    });
};
/* called a selector and allows to select values from the state */
export const selectLogin = (state) => {
  return state.auth;
};
export const selectLoggedinCompany = (state) => {
  return state.auth?.profile?.CompanyID || null;
};
export const selectLoggedinCompanyKey = (state) => {
  return state.auth?.profile?.CompanyKey || null;
};
export const selectSessionExpired = (state) => {
  return state.auth?.sessionExpired;
};
export const getLoggedinCompanyBusinessCategory = (state) => {
  return state.auth?.profile?.businessCategory || null;
};

export const getLoggedinUser = (state) => {
  return state.auth?.profile;
};

export const verifyJwt = (payload) => {
  let jwtVerifyAPI = `${JWT_VERIFY}?jwt=${payload}`;
  return ApiServices.post(jwtVerifyAPI, {});
};

export const getProfileData = (payload) => {
  let authAPI = ExchangeRefereshToken(payload);
  return ApiServices.post(authAPI, {});
};

export const getAppMetaAsync = (payload) => {
  return ApiServices.getWithParams(HostAPI.getMeta(payload?.data));
};

export const selectDynamicMeta = (state) => {
  return state.auth?.dynamicMeta;
};
export const getUserCompaniesCount = (state) => {
  return state.auth?.userCompaniesCount;
};

export default authSlice.reducer;
